<template>
  <div class="full-page-width relative locations" v-if="get_webhook">
    <ConfirmationBar
      :showSavebar="showSavebar"
      @confirm="submitForm"
      @cancel="cancelSave" />

    <div class="add-button c-float m-b-25">
      <div class="flex-center float-left">
        <Btn
          @click.native="routeIt"
          class="light bordered empty d-inline-block m-r-15 fix-40"
          :icon="{ name: 'arrowLeftBig', size: '20' }" />
        <p class="user-name">{{ get_webhook.name || get_webhook.url }}</p>
      </div>
    </div>

    <!-- GENERAL -->
    <div class="row">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :title="$t('general')"
          :text="$t('webhook-general-info-text')"
          :btnText="$t('delete')"
          btnType="red"
          btnIcon="deleteForever"
          @action="toggleDeletePopup" />
      </div>
      <div class="col-xs-8">
        <div class="card">
          <div class="toggle-card m-b-30">
            <div>
              <span class="title">{{ $t('status') }}</span>
              <span class="additional-info">{{ $t('webhook_enable_text') }}</span>
            </div>
            <Toggle id="webhookIsActive" v-model="form.active" />
          </div>
          <div class="m-b-30">
            <InputSelect
              :label="$t('event')"
              v-model="form.event"
              :shouldTranslate="false"
              :options="eventOptions" />
            <small class="field-hint" v-if="selectedEventDescription">
              {{ selectedEventDescription }}
            </small>
          </div>
          <InputText
            class="m-b-30"
            :label="$t('name')"
            v-model="form.name" />
          <InputText
            :label="$t('endpoint_url')"
            v-model="form.url" />
          <small class="field-hint">{{ $t('webhook_url_hint') }}</small>
        </div>
      </div>
    </div>

    <div class="separator"></div>

    <!-- STATUS FILTER — only for events that support one -->
    <div class="row" v-if="supportsStatusFilter">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :title="$t('shipment_statuses')"
          :text="$t('webhook-statuses-info-text')"
          :showBtn="false" />
      </div>
      <div class="col-xs-8">
        <div class="card">
          <InputSelect
            :multiSelect=true
            :preselected="selectedStatuses"
            :label="$t('trigger_on_statuses')"
            :shouldTranslate="false"
            @change="selectStatuses"
            :options="statusOptions" />
          <small class="field-hint">{{ $t('webhook_statuses_empty_hint') }}</small>
        </div>
      </div>
    </div>

    <div class="separator" v-if="supportsStatusFilter"></div>

    <!-- SECURITY -->
    <div class="row">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :title="$t('security')"
          :text="$t('webhook-security-info-text')"
          :btnText="$t('download_api_documentation')"
          btnIcon="cloudDownload"
          @action="downloadDocumentation" />
      </div>
      <div class="col-xs-8">
        <div class="card">
          <label class="field-label">{{ $t('signing_secret') }}</label>
          <div class="webhook-secret m-b-20">
            <code>{{ secretDisplay }}</code>
          </div>
          <Btn
            @click.native="showSecret = !showSecret"
            class="light bordered icon-text-btn d-inline-block m-r-10"
            :icon="{ name: 'preview', size: '18' }"
            :btnText="showSecret ? $t('hide') : $t('reveal')" />
          <Btn
            @click.native="copySecret"
            class="light bordered icon-text-btn d-inline-block m-r-10"
            :icon="{ name: 'fileCopies', size: '18' }"
            :btnText="$t('copy')" />
          <Btn
            @click.native="toggleRegeneratePopup"
            class="light bordered icon-text-btn d-inline-block"
            :icon="{ name: 'restore', size: '18' }"
            :btnText="$t('regenerate_secret')" />
          <small class="field-hint">{{ $t('webhook_signature_hint') }}</small>
        </div>
      </div>
    </div>

    <div class="separator"></div>

    <!-- DELIVERIES -->
    <div class="row m-b-40">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :title="$t('recent_deliveries')"
          :text="$t('webhook-deliveries-info-text')"
          :btnText="$t('send_test_event')"
          btnIcon="send"
          @action="sendTest" />
      </div>
      <div class="col-xs-8">
        <!-- Not a .toggle-card: that is space-between, which pushes the switch to the far edge of
             the column and detaches it from its label once the description runs to two lines. -->
        <div class="deliveries-filter m-b-20">
          <div class="deliveries-filter-control">
            <span class="deliveries-filter-label">{{ $t('show_all_deliveries') }}</span>
            <Toggle id="showAllDeliveries" v-model="showAllDeliveries" @change="fetchDeliveries" />
          </div>
          <small class="deliveries-filter-hint">{{ $t('show_all_deliveries_hint') }}</small>
        </div>
        <ul class="list-table">
          <li
            class="list-table-item"
            v-for="(delivery, index) in get_webhook_deliveries" :key="`delivery-${index}`">
            <div class="table-cell small">
              <div class="list-icon-block">
                <div class="status-indicator" :class="deliveryStatusClass(delivery.status)"></div>
                <Icon :circle=true name="send" size="20" />
              </div>
            </div>
            <div class="table-cell delivery-cell">
              <p>
                <span class="main-info">{{ delivery.created_at }}</span>
                <span class="additional-info">{{ delivery.event }}</span>
              </p>
            </div>
            <div class="table-cell delivery-cell">
              <p>
                <span class="main-info">{{ $t(delivery.status) }}</span>
                <span class="additional-info">
                  {{ $t('attempt') }} {{ delivery.attempt }}
                  <template v-if="delivery.response_code"> &middot; HTTP {{ delivery.response_code }}</template>
                </span>
              </p>
            </div>
            <div class="table-cell delivery-cell">
              <p>
                <span class="additional-info">{{ delivery.error_message || '-' }}</span>
              </p>
            </div>
            <div class="table-cell small text-right">
              <Btn
                v-if="delivery.status === 'failed'"
                @click.native="redeliver(delivery.id)"
                class="light bordered"
                position="icon-pos-center"
                :icon="{ name: 'restore', size: '16' }" />
            </div>
          </li>
        </ul>
        <p class="c-light m-t-20" v-if="!get_webhook_deliveries.length">
          {{ showAllDeliveries ? $t('no_deliveries_yet') : $t('no_deliveries_need_attention') }}
        </p>
        <small class="field-hint" v-else>
          {{ $t('deliveries_capped', { count: deliveriesLimit }) }}
        </small>
      </div>
    </div>

    <Popup
      v-if="openDeletePopup"
      :popupSmall=true
      min-height="150px"
      :showDeleteButton="true"
      :showConfirmButton="false"
      @cancel="toggleDeletePopup"
      @deleteAction="submitDelete">
      <template v-slot:header>
        <h2>{{ $t('delete') }}</h2>
      </template>
      <div>
        <div class="row">
          <div class="col-xs-12">
            <p>{{ $t('are_you_sure_delete_text') }}</p>
          </div>
        </div>
      </div>
    </Popup>

    <Popup
      v-if="openRegeneratePopup"
      :popupSmall=true
      min-height="150px"
      @cancel="toggleRegeneratePopup"
      @confirm="submitRegenerate">
      <template v-slot:header>
        <h2>{{ $t('regenerate_secret') }}</h2>
      </template>
      <div>
        <div class="row">
          <div class="col-xs-12">
            <p>{{ $t('webhook_regenerate_secret_warning') }}</p>
          </div>
        </div>
      </div>
    </Popup>
  </div>
</template>
<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import InputSelect from '@/components/elements/inputs/select.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import Popup from '@/components/global/popup';
import InfoTextBox from '@/components/global/infoTextBox';
import ConfirmationBar from '@/components/global/confirmationBar';

export default {
  name: 'WebhooksView',
  components: { Btn, Popup, InputText, InputSelect, Toggle, InfoTextBox, ConfirmationBar },
  data() {
    return {
      showSavebar: false,
      savebarEnabled: false,
      showSecret: false,
      openDeletePopup: false,
      openRegeneratePopup: false,
      // Default view is pending+failed only — a high-volume company with no status filter on its
      // subscription can produce far more successes than the page size, which would otherwise
      // bury a real failure under a wall of deliveries. See fetchDeliveries().
      showAllDeliveries: false,
      deliveriesPollingInterval: null,
      // Sent explicitly rather than relying on the API's own default, so the "showing last N"
      // note below the list can never claim a different number than we actually asked for.
      deliveriesLimit: 50,
      form: {
        event: '',
        name: '',
        url: '',
        active: true,
        config_json: { statuses: [] },
      },
    };
  },
  computed: {
    ...mapGetters([
      'get_webhook',
      'get_webhook_events',
      'get_webhook_deliveries',
      'get_webhook_event_definition',
    ]),
    eventOptions() {
      return this.get_webhook_events.events.map((definition) => ({
        text: definition.event,
        value: definition.event,
      }));
    },
    // The multiselect compares against String(option.value), so the ids have to be strings here.
    statusOptions() {
      return this.get_webhook_events.statuses.map((status) => ({
        text: status.name,
        value: String(status.id),
      }));
    },
    selectedStatuses() {
      return (this.form.config_json.statuses || []).map((id) => String(id));
    },
    selectedEventDefinition() {
      return this.get_webhook_event_definition(this.form.event);
    },
    selectedEventDescription() {
      return this.selectedEventDefinition ? this.selectedEventDefinition.description : '';
    },
    supportsStatusFilter() {
      return !!(this.selectedEventDefinition && this.selectedEventDefinition.supports_status_filter);
    },
    secretDisplay() {
      if (!this.get_webhook || !this.get_webhook.secret) return '';

      return this.showSecret ? this.get_webhook.secret : '•'.repeat(this.get_webhook.secret.length);
    },
  },
  created() {
    this.$store.dispatch('GET_WEBHOOK_EVENTS');
    this.$store.dispatch('GET_WEBHOOK', { id: this.$route.params.id }).then((webhook) => {
      if (webhook) this.populateForm(webhook);
      // savebarEnabled only after the form is filled, otherwise populating it would immediately
      // trip the watchers and show an unsaved-changes bar on a page the user has not touched.
      this.$nextTick(() => {
        this.savebarEnabled = true;
      });
    });
    this.fetchDeliveries();
    this.startDeliveriesPolling();
  },
  beforeDestroy() {
    this.stopDeliveriesPolling();
  },
  methods: {
    populateForm(webhook) {
      this.form = {
        event: webhook.event,
        name: webhook.name || '',
        url: webhook.url,
        active: !!webhook.active,
        config_json: {
          statuses: (webhook.config_json && webhook.config_json.statuses) || [],
        },
      };
    },
    routeIt() {
      this.$router.push({ path: '/settings/integration/webhooks' });
    },
    selectStatuses(values) {
      this.form.config_json = { ...this.form.config_json, statuses: values.map((id) => Number(id)) };
    },
    cancelSave() {
      this.showSavebar = false;
      this.savebarEnabled = false;
      this.populateForm(this.get_webhook);
      this.$nextTick(() => {
        this.savebarEnabled = true;
      });
    },
    submitForm() {
      if (!this.form.url || !this.form.event) return;

      // An event that has no status filter must not carry stale statuses from a previous event
      // choice — they would be dead config the backend ignores but the UI keeps showing.
      const form = { ...this.form };

      if (!this.supportsStatusFilter) {
        form.config_json = { statuses: [] };
      }

      this.$store.dispatch('EDIT_WEBHOOK', { id: this.$route.params.id, form }).then((webhook) => {
        this.showSavebar = false;
        if (webhook) this.populateForm(webhook);
      }).catch(() => {});
    },
    toggleDeletePopup() {
      this.openDeletePopup = !this.openDeletePopup;
    },
    submitDelete() {
      this.$store.dispatch('DELETE_WEBHOOK', { id: this.$route.params.id }).then(() => {
        this.toggleDeletePopup();
        this.routeIt();
      });
    },
    toggleRegeneratePopup() {
      this.openRegeneratePopup = !this.openRegeneratePopup;
    },
    submitRegenerate() {
      this.$store.dispatch('REGENERATE_WEBHOOK_SECRET', { id: this.$route.params.id }).then(() => {
        this.openRegeneratePopup = false;
        this.showSecret = true;
      });
    },
    copySecret() {
      if (!this.get_webhook || !this.get_webhook.secret) return;

      navigator.clipboard.writeText(this.get_webhook.secret);
    },
    sendTest() {
      // Force the unfiltered view for a test send: the default list only shows pending/failed, so
      // a test that succeeds immediately would otherwise vanish instead of confirming anything.
      this.showAllDeliveries = true;
      this.$store.dispatch('SEND_WEBHOOK_TEST', { id: this.$route.params.id }).then(() => {
        this.refreshDeliveries();
      });
    },
    redeliver(deliveryId) {
      this.$store.dispatch('REDELIVER_WEBHOOK', { id: deliveryId }).then(() => {
        this.refreshDeliveries();
      });
    },
    fetchDeliveries() {
      this.$store.dispatch('GET_WEBHOOK_DELIVERIES', {
        id: this.$route.params.id,
        status: this.showAllDeliveries ? 'all' : undefined,
        limit: this.deliveriesLimit,
      });
    },
    // Keeps pending deliveries moving to delivered/failed without the admin having to reload —
    // retries can land anywhere from a minute to hours later (WebhookDelivery::RETRY_BACKOFF_SECONDS),
    // so this is a plain poll, not something that could instead be driven off a single callback.
    startDeliveriesPolling() {
      this.deliveriesPollingInterval = setInterval(() => {
        this.fetchDeliveries();
      }, 5000);
    },
    stopDeliveriesPolling() {
      if (this.deliveriesPollingInterval) {
        clearInterval(this.deliveriesPollingInterval);
        this.deliveriesPollingInterval = null;
      }
    },
    // The queue worker sends asynchronously, so an immediate refetch would still show `pending`.
    refreshDeliveries() {
      setTimeout(() => {
        this.fetchDeliveries();
      }, 1500);
    },
    deliveryStatusClass(status) {
      const classes = {
        pending: 'pending',
        delivered: 'active',
        failed: 'expired',
      };

      return classes[status] || 'draft';
    },
    downloadDocumentation() {
      this.$store.dispatch('DOWNLOAD_WEBHOOK_DOCUMENTATION', { format: 'md' });
    },
  },
  watch: {
    'form.event': function () {
      if (this.savebarEnabled) this.showSavebar = true;
    },
    'form.name': function () {
      if (this.savebarEnabled) this.showSavebar = true;
    },
    'form.url': function () {
      if (this.savebarEnabled) this.showSavebar = true;
    },
    'form.active': function () {
      if (this.savebarEnabled) this.showSavebar = true;
    },
    'form.config_json': {
      handler() {
        if (this.savebarEnabled) this.showSavebar = true;
      },
      deep: true,
    },
  },
};
</script>
<style scoped>
/* .bordered (button.scss) sets padding: 12px 28px, overwriting the base .button rule's
   padding-left: 40px that normally reserves room for the absolutely-positioned .btn-icon
   (left: 15px). Every other bordered+icon button in the app is icon-only (fix-40 or
   icon-pos-center, no text), so this never surfaced before: with text present, the icon renders
   on top of the first letters. Restore the icon clearance for just these buttons rather than
   touching the shared component. */
.icon-text-btn.bordered {
  padding-left: 40px;
}

/* Matches .input-text label / .input-select label (inputs.scss, select.scss) exactly, so the
   "Signing secret" label — which isn't part of an actual InputText/InputSelect, just a plain
   <label> above a read-only code box — doesn't stand out at the browser-default <label> size. */
.field-label {
  display: block;
  margin-bottom: 9px;
  color: #363d44;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3em;
}

/* .c-light (used elsewhere in the app, e.g. addTemplate.vue) isn't defined anywhere in the SCSS —
   it's a no-op, so hint text under a field fell back to the ambient (dark, input-sized) text with
   no gap above it. Defined here instead of touching the shared stylesheet, since .c-light's exact
   fallback behavior is relied on unchanged elsewhere. font-size/color match the existing "light"
   text used in InfoTextBox (info-text-box.scss: color: $color-light, i.e. #989fa6). */
.field-hint {
  display: block;
  margin-top: 8px;
  color: #989fa6;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
}

/* Description sits above so the label can sit directly beside the switch, instead of .toggle-card's
   space-between stranding them at opposite ends of the column. */
.deliveries-filter-hint {
  display: block;
  margin-bottom: 10px;
  color: #989fa6;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
}

.deliveries-filter-control {
  display: flex;
  align-items: center;
}

.deliveries-filter-label {
  margin-right: 12px;
  font-size: 14px;
  font-weight: 500;
}

/* .list-table-item's .table-cell (list-table.scss) is flex-grow:1 with flex-basis:auto and no
   gap/margin between cells except the .small variant — fine everywhere else in the app, where a
   row only ever has two short content cells. This row has three: date/event, status/attempt, and
   the delivery error message, which can be long and contain unbreakable tokens (a URL). With no
   gap and flex-basis:auto, that cell's own content width wins over its fair share and runs flush
   into its neighbour. flex-basis:0 + min-width:0 makes all three share space equally regardless of
   content, and overflow-wrap lets the long URL wrap inside its own column instead of forcing it
   wider. Scoped to this list only, not the shared component. */
.delivery-cell {
  flex: 1 1 0;
  min-width: 0;
  padding-right: 20px;
  overflow-wrap: anywhere;
}

.webhook-secret {
  padding: 12px 16px;
  background: #f4f6f8;
  border: 1px solid #dbdfe3;
  border-radius: 4px;
  word-break: break-all;
}

.webhook-secret code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 13px;
}
</style>
